import React, { useEffect, useState, useRef, createRef } from 'react';
import * as Bixby from '$/core';
import InputBlock from '$/components/global/inputBlock';
import { Formik, Field, ErrorMessage, FormikProps } from 'formik';
import * as Yup from 'yup';
import { AppState } from '$/store';
import Modal from '$/components/global/modal';
import { connect } from 'react-redux';
import { createUser } from '$/store/user/actions';
import { getClients } from '$/store/client/actions';
import './index.scss';
import BixCheckbox from '$/components/global/bixCheckbox';

interface IAddUserProps {
  clients?: Bixby.Models.ClientSlim[];
  active: boolean;
  onAddSubmit: () => void;
  onCloseModal: () => void;
  dispatchCreateUser: (user: Bixby.Models.CreateUserRequest) => Promise<void>;
  dispatchGetClients: () => Promise<void>;
}

interface UserValues {
  client_Id: string;
  isAdminUser: boolean;
  email: string;
  firstName: string;
  lastName: string;
  emailDomain: string;
  isClientConsultant: boolean;
}

const AddUser: React.FC<IAddUserProps> = props => {
  const { clients, active, onAddSubmit, onCloseModal, dispatchCreateUser, dispatchGetClients } = props;
  const [emailDomains, setEmailDomains] = useState(Bixby.Configuration.adminDomains.sort((a, b) => a.localeCompare(b)));
  const initialValues = {
    client_Id: '',
    isAdminUser: true,
    email: '',
    firstName: '',
    lastName: '',
    isClientConsultant: false,
    emailDomain: '',
  } as UserValues;

  useEffect(() => {
    dispatchGetClients();
  }, [dispatchGetClients]);

  const formikRef = useRef<FormikProps<UserValues>>(null);

  const clientRef = createRef<HTMLInputElement>();

  const handleCloseModal = () => {
    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }

    if (clientRef && clientRef.current) {
      clientRef.current.value = '';
    }

    onCloseModal();
  };

  const domainOptions = emailDomains.map(domain => {
    return (
      <option value={domain} key={domain}>
        {domain}
      </option>
    );
  });

  const RadioBox = ({ id, name, label, checked, ...props }: { [key: string]: any }) => (
    <div className="bix_radio_btn">
      <input type="radio" id={id} name={name} checked={checked} {...props} />
      <label htmlFor={id}>
        <span className="checked" />
        {label}
      </label>
    </div>
  );

  const schema = Yup.object({
    firstName: Yup.string().max(256, 'Too long').required('First name is required.'),
    lastName: Yup.string().max(256, 'Too long').required('Last name is required.'),
    email: Yup.string().max(256, 'Too long').required('User ID is required.'),
    client_Id: Yup.string().when('isAdminUser', {
      is: false,
      then: schema => schema.required('Client is required if not an Admin'),
      otherwise: schema => schema.notRequired(),
    }),
    isAdminUser: Yup.boolean(),
    emailDomain: Yup.string().required('Email Domain is required.'),
  });

  return (
    <Modal title="Add New User" active={active} forForm onClose={handleCloseModal}>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={schema}
        // tslint:disable-next-line: jsx-no-lambda
        onSubmit={(values: UserValues, { resetForm }) => {
          const client = values.client_Id === '' ? null : values.client_Id;
          const newUser = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: `${values.email}@${values.emailDomain}`,
            isAdminUser: values.isAdminUser,
            client_Id: client,
            isClientConsultant: values.isClientConsultant,
          };

          dispatchCreateUser(newUser);
          onAddSubmit();
          resetForm();
        }}
        render={({ handleSubmit, values, handleBlur, setFieldValue }) => (
          <form className="create-user-form" onSubmit={handleSubmit}>
            <Field
              name="isAdminUser"
              render={({ field }: { [key: string]: any }) => {
                const handleIsAdminClicked = (e: React.ChangeEvent<HTMLInputElement>) => {
                  if (e.currentTarget.value) {
                    setEmailDomains(Bixby.Configuration.adminDomains);
                    setFieldValue('client_Id', '');
                    setFieldValue('industry_Id', '');
                    setFieldValue('title_Id', '');
                    setFieldValue('isClientConsultant', false);

                    if (clientRef && clientRef.current) {
                      clientRef.current.value = '';
                    }
                  }
                };
                return (
                  <div className="radio_button_group">
                    <RadioBox
                      {...field}
                      onChange={() => setFieldValue('isAdminUser', false)}
                      checked={!values.isAdminUser}
                      id="isClientUser"
                      label="This user is a client"
                    />
                    <RadioBox
                      {...field}
                      checked={!!values.isAdminUser}
                      id="isAdminUser"
                      onChange={e => {
                        setFieldValue('isAdminUser', true);
                        handleIsAdminClicked(e);
                      }}
                      label="This user is an admin"
                    />
                  </div>
                );
              }}
            />
            <Field
              name="client_Id"
              render={({ form }: { [key: string]: any }) => {
                const handleClientChange = (ev: React.FormEvent<HTMLInputElement>) => {
                  if (clients) {
                    const selected = clients.find(c => c.name === ev.currentTarget.value);
                    if (selected) {
                      form.setFieldValue('client_Id', selected.id);
                      setEmailDomains(selected.emailDomain.split(','));
                    }
                  }
                };
                return (
                  <InputBlock title="Client">
                    <input
                      ref={clientRef}
                      list="clients"
                      id="client_Id"
                      name="client_Id"
                      onBlur={handleBlur}
                      onChange={handleClientChange}
                      disabled={values.isAdminUser}
                      autoComplete="off"
                    />
                    <datalist id="clients">
                      {clients &&
                        clients.map((result, ix) => {
                          return (
                            <option key={ix} label={result.name} value={result.name}>
                              {result.name}
                            </option>
                          );
                        })}
                    </datalist>
                  </InputBlock>
                );
              }}
            />
            <ErrorMessage name="client_Id">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
            <div className="bix_checkbox_form">
              <BixCheckbox
                id="isClientConsultant"
                checked={values.isClientConsultant}
                onChange={event => {
                  const target = event.target as HTMLInputElement;
                  setFieldValue('isClientConsultant', target.checked);
                }}
                disabled={values.isAdminUser}>
                Is Client Consultant
              </BixCheckbox>
            </div>
            <InputBlock title="First Name*">
              <Field type="text" name="firstName" />
            </InputBlock>
            <ErrorMessage name="firstName">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
            <InputBlock title="Last Name*">
              <Field type="text" name="lastName" />
            </InputBlock>
            <ErrorMessage name="lastName">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
            <InputBlock title="User ID*">
              <Field type="text" className="email_prefix" name="email" />
              <Field name="emailDomain" className="email_domain" component="select">
                <option value="" key="-1" disabled>
                  Select a Domain...
                </option>
                {domainOptions}
              </Field>
            </InputBlock>
            <ErrorMessage name="email">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
            <div className="button_container">
              <button className="bix_button" type="submit">
                Save User
              </button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.userState.isLoading,
    error: state.userState.error,
    clients: state.clientState.clients,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchCreateUser: (providerName: Bixby.Models.CreateUserRequest) => dispatch(createUser(providerName)),
  dispatchGetClients: () => dispatch(getClients()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddUser);
